'use client';

import React from 'react';
import { RadialBarChart, RadialBar, ResponsiveContainer } from 'recharts';
import { TrendingDown, TrendingUp, Activity } from 'lucide-react';
import type { NetworkHealth } from '@/hooks/useDashboardRealtime';

interface Props {
  health: NetworkHealth | null;
  loading: boolean;
  lastUpdated: Date | null;
}

export default function HealthScoreCard({ health, loading, lastUpdated }: Props) {
  const score = health?.score ?? 73;
  const trendDelta = health?.trend_delta ?? -8;

  const data = [
    { name: 'score', value: score, fill: score >= 90 ? '#22c55e' : score >= 70 ? '#f59e0b' : '#ef4444' },
  ];

  const color = score >= 90 ? 'text-success' : score >= 70 ? 'text-warning' : 'text-danger';
  const label = score >= 90 ? 'Good' : score >= 70 ? 'Degraded' : 'Critical';

  const secondsAgo = lastUpdated
    ? Math.round((Date.now() - lastUpdated.getTime()) / 1000)
    : null;

  const updatedLabel = loading
    ? 'Connecting…'
    : secondsAgo !== null
    ? secondsAgo < 5
      ? 'Just now'
      : `${secondsAgo}s ago`
    : 'Updated 12s ago';

  return (
    <div className="bg-surface border border-border rounded-xl p-5 flex flex-col items-center justify-between min-h-[200px] relative overflow-hidden">
      <div className="absolute inset-0 opacity-5">
        <div className="w-full h-full" style={{
          backgroundImage: 'radial-gradient(circle at 50% 50%, hsl(38 92% 50%) 0%, transparent 70%)'
        }} />
      </div>
      <div className="flex items-center justify-between w-full mb-2">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-widest text-muted">Network Health</p>
          <p className="text-[10px] text-muted mt-0.5">Composite score</p>
        </div>
        <span className={`flex items-center gap-1 text-[10px] font-semibold ${trendDelta < 0 ? 'text-warning' : 'text-success'} bg-warning/10 px-2 py-0.5 rounded-full`}>
          {trendDelta < 0 ? <TrendingDown size={10} /> : <TrendingUp size={10} />}
          {trendDelta > 0 ? `+${trendDelta}` : trendDelta} pts
        </span>
      </div>

      <div className="relative w-32 h-32">
        {loading ? (
          <div className="w-full h-full flex items-center justify-center">
            <div className="w-16 h-16 rounded-full border-4 border-border border-t-primary animate-spin" />
          </div>
        ) : (
          <ResponsiveContainer width="100%" height="100%">
            <RadialBarChart
              cx="50%"
              cy="50%"
              innerRadius="65%"
              outerRadius="90%"
              barSize={8}
              data={data}
              startAngle={225}
              endAngle={-45}
            >
              <RadialBar dataKey="value" cornerRadius={4} background={{ fill: 'hsl(222 14% 20%)' }} />
            </RadialBarChart>
          </ResponsiveContainer>
        )}
        {!loading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center">
            <span className={`text-3xl font-bold font-mono-data ${color}`}>{score}</span>
            <span className={`text-[10px] font-semibold ${color}`}>{label}</span>
          </div>
        )}
      </div>

      <div className="flex items-center gap-1.5 text-[10px] text-muted mt-1">
        <Activity size={10} className={loading ? 'animate-pulse text-primary' : ''} />
        <span>{updatedLabel}</span>
        {!loading && (
          <span className="w-1.5 h-1.5 rounded-full bg-success animate-pulse ml-1" />
        )}
      </div>
    </div>
  );
}